import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  ImpressionJob,
  ImpressionJobFilters,
  JobAnalytics,
  ImpressionJobStats,
} from "../types/impressionJob";
import { apiService } from "../services/api";

interface ImpressionJobState {
  // Data
  jobs: ImpressionJob[];
  selectedJob: ImpressionJob | null;
  currentAnalytics: JobAnalytics | null;
  stats: ImpressionJobStats | null;

  // Pagination metadata
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;

  // Filters
  filters: ImpressionJobFilters;

  // Loading states
  loading: boolean;
  refreshing: boolean;
  analyticsLoading: boolean;

  // Actions
  fetchJobs: (
    filters?: ImpressionJobFilters,
    page?: number,
    limit?: number
  ) => Promise<void>;
  selectJob: (job: ImpressionJob | null) => void;
  createJob: (campaignId: string) => Promise<ImpressionJob>;
  refreshJobStatus: (jobId: number) => Promise<void>;
  fetchJobAnalytics: (jobId: number) => Promise<void>;
  deleteJob: (jobId: number) => Promise<void>;
  fetchStats: () => Promise<void>;
  setFilters: (filters: ImpressionJobFilters) => void;
  clearAnalytics: () => void;
  refreshJobs: () => Promise<void>;
}

export const useImpressionJobStore = create<ImpressionJobState>()(
  devtools(
    (set, get) => ({
      // Initial state
      jobs: [],
      selectedJob: null,
      currentAnalytics: null,
      stats: null,
      totalCount: 0,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 20,
      filters: {},
      loading: false,
      refreshing: false,
      analyticsLoading: false,

      // Actions
      fetchJobs: async (filters = {}, page = 1, limit = 20) => {
        set({ loading: true, filters });
        try {
          const response = await apiService.getImpressionJobs({
            ...filters,
            page,
            limit,
          });

          set({
            jobs: response.jobs,
            totalCount: response.pagination.total,
            currentPage: response.pagination.page,
            totalPages: response.pagination.totalPages,
            itemsPerPage: response.pagination.limit,
            loading: false,
          });
        } catch (error) {
          console.error("Error fetching impression jobs:", error);
          set({ loading: false });
          throw error;
        }
      },

      selectJob: (job) => {
        set({ selectedJob: job, currentAnalytics: null });
      },

      createJob: async (campaignId: string) => {
        try {
          const newJob = await apiService.createImpressionJob({
            campaign_id: campaignId,
          });
          const { jobs } = get();
          set({ jobs: [newJob, ...jobs] });
          return newJob;
        } catch (error) {
          console.error("Error creating impression job:", error);
          throw error;
        }
      },

      refreshJobStatus: async (jobId: number) => {
        set({ refreshing: true });
        try {
          const updatedJob = await apiService.refreshImpressionJobStatus(jobId);
          const { jobs, selectedJob } = get();

          // Update in list
          const updatedJobs = jobs.map((job) =>
            job.id === jobId ? updatedJob : job
          );

          // Update selected job if it's the one being refreshed
          const newSelectedJob =
            selectedJob?.id === jobId ? updatedJob : selectedJob;

          set({
            jobs: updatedJobs,
            selectedJob: newSelectedJob,
            refreshing: false,
          });
        } catch (error) {
          console.error("Error refreshing job status:", error);
          set({ refreshing: false });
          throw error;
        }
      },

      fetchJobAnalytics: async (jobId: number) => {
        set({ analyticsLoading: true });
        try {
          const analytics = await apiService.getImpressionJobAnalytics(jobId);
          set({
            currentAnalytics: analytics,
            selectedJob: analytics.job,
            analyticsLoading: false,
          });
        } catch (error) {
          console.error("Error fetching job analytics:", error);
          set({ analyticsLoading: false });
          throw error;
        }
      },

      deleteJob: async (jobId: number) => {
        try {
          await apiService.deleteImpressionJob(jobId);
          const { jobs, selectedJob } = get();

          const filteredJobs = jobs.filter((job) => job.id !== jobId);

          // Clear selected job if it's the one being deleted
          const newSelectedJob = selectedJob?.id === jobId ? null : selectedJob;

          set({
            jobs: filteredJobs,
            selectedJob: newSelectedJob,
            currentAnalytics: null,
          });
        } catch (error) {
          console.error("Error deleting impression job:", error);
          throw error;
        }
      },

      fetchStats: async () => {
        try {
          const stats = await apiService.getImpressionJobStats();
          set({ stats });
        } catch (error) {
          console.error("Error fetching job stats:", error);
          throw error;
        }
      },

      setFilters: (filters) => {
        set({ filters });
      },

      clearAnalytics: () => {
        set({ currentAnalytics: null });
      },

      refreshJobs: async () => {
        const { fetchJobs, filters, currentPage, itemsPerPage } = get();
        await fetchJobs(filters, currentPage, itemsPerPage);
      },
    }),
    { name: "impression-job-store" }
  )
);
